import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { useCart } from "../../context/CartContext";
import cartAPI from "../../api/cartApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import Breadcrump from "../../components/Breadcrump";
import { useSiteConfig } from "../../context/SiteConfigContext";

// ─── Icons ────────────────────────────────────────────────────────────────────
const MinusIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <path d="M2 5h6" />
  </svg>
);
const PlusIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <path d="M5 2v6M2 5h6" />
  </svg>
);
const ArrowRight = () => (
  <svg
    className="cp-arrow"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M1 6h10M7 1l5 5-5 5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const BagIcon = () => (
  <svg
    width="52"
    height="52"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="0.8"
  >
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);
const TrashIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="1 3 11 3" />
    <path d="M4.5 3V2a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1" />
    <path d="M2 3l.6 7.5A1 1 0 0 0 3.6 11h4.8a1 1 0 0 0 1-.5L10 3" />
  </svg>
);

const FREE_THRESHOLD = 10000;

// ─── CartPageItem — luxury UI, CartItems.jsx logic ────────────────────────────
const CartPageItem = memo(function CartPageItem({ item }) {
  const { updateQuantity, removeItem } = useCart();

  const { _id, thumbnail, slug, title, variantName, quantity, price, mrp } =
    item;

  const numPrice = Number(price);
  const numMrp = Number(mrp);

  const handleRemove = useCallback(() => removeItem(_id), [removeItem, _id]);

  const handleQty = useCallback(
    (delta) => {
      const next = quantity + delta;
      if (next < 1) return;
      updateQuantity(_id, next);
    },
    [updateQuantity, _id, quantity],
  );

  return (
    <div className="cart-page-item">
      {/* Thumbnail */}
      <div className="cart-page-thumb">
        <div className="cart-page-thumb-pattern" aria-hidden="true" />
        {thumbnail && (
          <img src={thumbnail} alt={title || slug} loading="lazy" />
        )}
      </div>

      {/* Info */}
      <div className="cart-page-item-info">
        {variantName && <p className="cart-page-item-craft">{variantName}</p>}
        <Link to={`/shop/${slug}`} className="cart-page-item-name">
          {title || slug}
        </Link>
        <button
          type="button"
          className="cart-page-remove-btn"
          onClick={handleRemove}
          aria-label={`Remove ${title || slug}`}
        >
          <TrashIcon /> Remove
        </button>
      </div>

      {/* Qty */}
      <div
        className="cart-page-qty"
        role="group"
        aria-label={`Quantity for ${title || slug}`}
      >
        <button
          type="button"
          className="cart-page-qty-btn"
          onClick={() => handleQty(-1)}
          disabled={quantity <= 1}
          aria-label="Decrease"
        >
          <MinusIcon />
        </button>
        <span className="cart-page-qty-value" aria-live="polite">
          {quantity}
        </span>
        <button
          type="button"
          className="cart-page-qty-btn"
          onClick={() => handleQty(+1)}
          aria-label="Increase"
        >
          <PlusIcon />
        </button>
      </div>

      {/* Price */}
      <div className="cart-page-item-price">
        <span className="cart-page-price">
          ₹{(numPrice * quantity).toLocaleString("en-IN")}
        </span>
        {!isNaN(numMrp) && numMrp > numPrice && (
          <span className="cart-page-price-original">
            ₹{(numMrp * quantity).toLocaleString("en-IN")}
          </span>
        )}
      </div>
    </div>
  );
});

// ─── Main Cart ────────────────────────────────────────────────────────────────
export default function Cart() {
  // ── API data ──────────────────────────────────────────────────────────────
  const { cartItems, cartSummary, fetchCart } = useCart();

  // ── Coupon state (from doc 9 logic) ──────────────────────────────────────
  const [couponList, setCouponList] = useState([]);
  const [couponCode, setCouponCode] = useState(cartSummary?.couponCode || "");
  const [activeCoupon, setActiveCoupon] = useState(
    cartSummary?.couponCode || "",
  );

   const { config } = useSiteConfig();
   console.log(config);

  // Sync coupon state when cartSummary updates
  useEffect(() => {
    setCouponCode(cartSummary?.couponCode || "");
    setActiveCoupon(cartSummary?.couponCode || "");
  }, [cartSummary]);

  useEffect(() => {
    fetchCouponCodes();
  }, []);

  const fetchCouponCodes = async () => {
    try {
      const res = await cartAPI.showCoupon();
      if (res.code === 200) setCouponList(res.data);
    } catch (_) {}
  };

  const applyCoupon = async (codeParam) => {
    const code = codeParam || couponCode;
    setActiveCoupon(code);
    setCouponCode(code);
    try {
      const res = await cartAPI.applyCoupon({ couponCode: code });
      if (res.code === 200) {
        await fetchCart();
        showSuccess("Coupon applied successfully");
      } else {
        setActiveCoupon("");
        showError(res.message || "Invalid coupon code");
      }
    } catch (err) {
      setActiveCoupon("");
      handleApiValidationError(err, showError);
    }
  };

  const removeCoupon = async () => {
    try {
      const res = await cartAPI.removeCoupon();
      if (res.code === 200) {
        setActiveCoupon("");
        setCouponCode("");
        fetchCart();
        showSuccess("Coupon removed");
      }
    } catch (_) {}
  };

  // ── Derived summary values from cartSummary (API) ────────────────────────
  const subtotal = Number(cartSummary?.subtotal) || 0;
  const discount = Number(cartSummary?.discount) || 0;
  const total = Number(cartSummary?.total) || subtotal;
  const totalQty = cartItems.reduce(
    (s, it) => s + (Number(it.quantity) || 1),
    0,
  );
  // const freeShip =
  //   cartSummary?.shipping === 0 ||
  //   cartSummary?.shipping === "0" ||
  //   subtotal >= FREE_THRESHOLD;
  const shipping = Number(cartSummary?.shipping) || 0;
  const freeShip = shipping === 0;
  const shipPct = Math.min(100, Math.round((subtotal / FREE_THRESHOLD) * 100));
  const remaining = Math.max(0, FREE_THRESHOLD - subtotal);

  const isEmpty = !cartItems || cartItems.length === 0;

  // ── GSAP refs ─────────────────────────────────────────────────────────────
  const eyebrowRef = useRef(null);
  const headingRef = useRef(null);
  const descRef = useRef(null);
  const labelsRef = useRef(null);
  const listRef = useRef(null);
  const summaryRef = useRef(null);
  const emptyRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.to(eyebrowRef.current, { opacity: 1, duration: 0.65 }, 0)
        .to(headingRef.current, { opacity: 1, y: 0, duration: 0.85 }, 0.18)
        .to(descRef.current, { opacity: 1, duration: 0.65 }, 0.4);

      if (!isEmpty) {
        tl.to(labelsRef.current, { opacity: 1, duration: 0.5 }, 0.5).to(
          summaryRef.current,
          { opacity: 1, y: 0, duration: 0.65 },
          0.55,
        );
        const cards =
          listRef.current?.querySelectorAll(".cart-page-item") ?? [];
        tl.to(cards, { opacity: 1, y: 0, duration: 0.55, stagger: 0.09 }, 0.55);
      } else if (emptyRef.current) {
        tl.to(emptyRef.current, { opacity: 1, duration: 0.65 }, 0.5);
      }
    });
    return () => ctx.revert();
  }, [isEmpty]);

  // Re-animate items on list change
  useEffect(() => {
    if (!listRef.current || isEmpty) return;
    gsap.to(listRef.current.querySelectorAll(".cart-page-item"), {
      opacity: 1,
      y: 0,
      duration: 0.4,
      stagger: 0.07,
      ease: "power3.out",
      overwrite: true,
    });
  }, [cartItems, isEmpty]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
        <Breadcrump title="Cart" />
      <main className="cart-page" aria-label="Shopping cart">
        <div className="container">
          <div className="row cart-page-header">
            <div className="col-12 col-md-8">
              {/* <p className="cart-page-eyebrow" ref={eyebrowRef}>
                <span className="cart-page-eyebrow-line" aria-hidden="true" />
                Shopping Bag
              </p> */}
              <h1 className="cart-page-heading" ref={headingRef}>
                Your <em>Cart</em>
              </h1>
              {/* <p className="cart-page-desc" ref={descRef}>
                Review your selected items before proceeding to checkout.
              </p> */}
            </div>
          </div>

          {/* ── Empty State ── */}
          {isEmpty ? (
            <div className="cart-page-empty" ref={emptyRef}>
              <span className="cart-page-empty-icon" aria-hidden="true">
                <BagIcon />
              </span>
              <h2 className="cart-page-empty-heading">Your cart is empty</h2>
              <p className="cart-page-empty-text">
                Discover our curated range of handcrafted ethnic pieces —
                something beautiful awaits.
              </p>
              <Link to="/shop" className="cart-page-empty-btn">
                <span>Start Shopping</span>
                <ArrowRight />
              </Link>
            </div>
          ) : (
            <div className="row g-4 g-lg-5">
              {/* ── Left: Items + Coupon ── */}
              <div className="col-12 col-lg-8">
                {/* Column Labels */}
                <div className="cart-col-labels" ref={labelsRef}>
                  <span className="cart-col-label">Product</span>
                  <span className="cart-col-label cart-col-label--qty">
                    Qty
                  </span>
                  <span className="cart-col-label cart-col-label--price">
                    Price
                  </span>
                </div>

                {/* Item List */}
                <div ref={listRef}>
                  {cartItems.map((item) => (
                    <CartPageItem key={item._id} item={item} />
                  ))}
                </div>

                {/* ── Coupon Section ── */}
                <div className="cart-coupon-section">
                  <span className="cart-coupon-label">Discount Code</span>
                  <div className="cart-coupon-input-row">
                    <input
                      className="cart-coupon-input"
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                    />
                    <button
                      type="button"
                      className={`cart-coupon-btn${activeCoupon ? " is-remove" : ""}`}
                      onClick={
                        activeCoupon ? removeCoupon : () => applyCoupon()
                      }
                    >
                      {activeCoupon ? "Remove" : "Apply"}
                    </button>
                  </div>

                  {/* Available coupons list */}
                  {couponList.length > 0 && (
                    <div className="cart-coupon-list">
                      {couponList.map((c) => (
                        <div key={c._id} className="cart-coupon-card">
                          <div className="cart-coupon-card-left">
                            <span className="cart-coupon-card-discount">
                              {c.discountType === "PERCENTAGE"
                                ? `${c.discountValue}%`
                                : `₹${c.discountValue}`}
                            </span>
                          </div>
                          <div className="cart-coupon-card-body">
                            <p className="cart-coupon-card-title">{c.title}</p>
                            <span className="cart-coupon-card-code">
                              Code: {c.code}
                            </span>
                          </div>
                          <div className="cart-coupon-card-action">
                            <button
                              type="button"
                              className={`cart-coupon-apply-btn${activeCoupon === c.code ? " is-applied" : ""}`}
                              onClick={() => applyCoupon(c.code)}
                            >
                              {activeCoupon === c.code ? "Applied ✓" : "Apply"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Right: Order Summary ── */}
              <div className="col-12 col-lg-4">
                <div className="cart-summary-card" ref={summaryRef}>
                  <h2 className="cart-summary-card-title">Order Summary</h2>

                  {/* Savings banner */}
                  {discount > 0 && (
                    <div className="cart-savings-tag">
                      <span aria-hidden="true">✦</span>
                      <span className="cart-savings-tag-text">
                        Saving ₹{Number(discount).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}

                  {/* Free shipping progress */}
                  {/* <div className="cart-ship-progress-wrap">
                    <div className="cart-ship-labels">
                      <span
                        className={
                          freeShip
                            ? "cart-summary-line-value--free"
                            : "cart-summary-line-value"
                        }
                      >
                        {freeShip
                          ? "Free"
                          : `₹${shipping.toLocaleString("en-IN")}`}
                      </span>
                      <span>{shipPct}%</span>
                    </div>
                    <div className="cart-ship-track" aria-hidden="true">
                      <div
                        className="cart-ship-fill"
                        style={{ width: `${shipPct}%` }}
                      />
                    </div>
                  </div> */}

                  {/* Summary rows */}
                  <div className="cart-summary-line">
                    <span className="cart-summary-line-label">
                      Subtotal ({totalQty} items)
                    </span>
                    <span className="cart-summary-line-value">
                      ₹{subtotal.toLocaleString("en-IN")}
                    </span>
                  </div>

                  {discount > 0 && (
                    <div className="cart-summary-line">
                      <span className="cart-summary-line-label">Discount</span>
                      <span className="cart-summary-line-value--savings">
                        −₹{Number(discount).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}

                  <div className="cart-summary-line">
                    <span className="cart-summary-line-label">Shipping</span>
                    <span
                      className={
                        freeShip
                          ? "cart-summary-line-value--free"
                          : "cart-summary-line-value"
                      }
                    >
                      {freeShip ? "Free" : `₹${shipping.toLocaleString("en-IN")}`}
                    </span>
                  </div>

                  <div className="cart-summary-total-row">
                    <span className="cart-summary-total-label">Total</span>
                    <span className="cart-summary-total-value">
                      ₹{total.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <span className="cart-summary-tax">
                    Taxes and duties calculated at checkout
                  </span>

                  <Link to="/checkout" className="cart-checkout-btn">
                    <span>Proceed to Checkout</span>
                    <ArrowRight />
                  </Link>
                  <Link to="/shop" className="cart-continue-link">
                    Continue Shopping
                    <ArrowRight />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
